import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { useStoreByName } from '../../../hooks/use-stores.hooks';
import EyeClose from '../../../assets/icons/iconEyeClose.svg';
import EyeOpen from '../../../assets/icons/iconEyeOpen.svg';
import ScoreStars from '../core/ScoreStars';
import ReadingModeTextArea from './ReadingModeTextArea';
import ScoreAndResultDialog from '../modals/ScoreAndResultModal';
import MicrophoneOffModal from '../modals/MicrophoneOffModal';
import GuestSignInPromptModal from '../modals/GuestSignInPromptModal';
import { useTranslation } from 'react-i18next';

interface ReadingModeContainerProps {
    mode: string;
    endHandler: (mode: string) => void;
    stats: any;
}

/** כל כמה זמן בודקים אם הפסוק הבא כבר נטען, וכמה זמן מחכים לו לכל היותר */
const ENTITY_CHANGE_POLL_MS = 150;
const ENTITY_CHANGE_TIMEOUT_MS = 4000;

/** מזהה יציב לקטע הנוכחי - לא כל הספרים מספקים id ייחודי, ולכן מצרפים גם index ותווית */
const getEntityKey = (entity: any): string =>
    `${entity?.id ?? ''}|${entity?.index ?? ''}|${entity?.label ?? ''}`;

/**
 * קומפוננטה presentation פשוטה למסגור תהליך הקריאה.
 * מציגה את הכותרות, אייקונים, סטטיסטיקות ומודל תוצאות.
 * כל הלוגיקה העיקרית נמצאת ב-ReadingModeTextArea.
 *
 * בנוסף היא מנצחת על "הזרימה הרציפה": כשההעדפה דלוקה, בסיום פסוק
 * האפליקציה חוזרת על אותו פסוק או עוברת לפסוק הבא בעצמה - בלי לחזור
 * למסך התצוגה המקדימה ובלי לפתוח את המיקרופון מחדש.
 *
 * @param mode - מצב הקריאה ('reading' או 'byHeart')
 * @param endHandler - פונקציה שנקראת בסיום הקריאה
 * @param stats - סטטיסטיקות משתמש להצגה
 */
const ReadingModeContainer: React.FC<ReadingModeContainerProps> = ({ mode, endHandler, stats }) => {
    const speechRecognitionStore = useStoreByName('speechRecognitionStore');
    const { score, clearData, releaseMicrophone } = speechRecognitionStore;
    const {
        isDoneListeningDialogOpen,
        setIsDoneListeningDialogOpen,
        isMicrophoneOffDialogOpen,
        setIsMicrophoneOffDialogOpen,
        autoFlowEnabled,
        readsPerVerse,
        isGuestSignInPromptPending,
        isGuestSignInPromptOpen,
        setIsGuestSignInPromptPending,
        setIsGuestSignInPromptOpen,
    } = useStoreByName('uiStore');
    const booksStore = useStoreByName('booksStore');
    const { goNextOrPrevEntity } = booksStore;
    const { t } = useTranslation();

    const [fade, setFade] = useState<"fade-in" | "fade-out">("fade-out");

    // מפתח שמאלץ אתחול מחדש של אזור הקריאה (פסוק חדש / חזרה על אותו פסוק)
    const [runKey, setRunKey] = useState(0);
    // מספר הקריאה הנוכחית על הפסוק הזה (1 = פעם ראשונה)
    const [readIndex, setReadIndex] = useState(1);
    // כמה פסוקים כבר נקראו ברצף הנוכחי - נותן למשתמש תחושת "כמה פסוקים ביחד"
    const [versesInFlow, setVersesInFlow] = useState(1);
    // ממתינים לטעינת הפסוק הבא
    const [isAdvancing, setIsAdvancing] = useState(false);
    // המשתמש עצר את הרצף בנגיעה במסך. ה-state הזה חי רק כל עוד נמצאים במצב
    // הקריאה: ההעדפה בתפריט לא משתנה, ולכן בהפעלה הבאה הזרימה חוזרת מעצמה.
    const [isAutoFlowStopped, setIsAutoFlowStopped] = useState(false);

    const isMountedRef = useRef(true);

    const isAutoFlowOn = autoFlowEnabled && !isAutoFlowStopped;
    const nextAutoAction: 'again' | 'next' = readIndex < readsPerVerse ? 'again' : 'next';

    /**
     * עצירת הרצף בנגיעה אחת - בכל מקום על מסך הקריאה.
     * הקריאה שרצה עכשיו ממשיכה כרגיל ומקבלת ציון; פשוט לא ממשיכים ממנה הלאה.
     */
    const stopAutoFlowOnTouch = useCallback(() => {
        if (!autoFlowEnabled || isAutoFlowStopped) return;
        setIsAutoFlowStopped(true);
    }, [autoFlowEnabled, isAutoFlowStopped]);

    const handleScoreCalculated = useCallback((scoreData: {
        score: number;
        executionTime: string;
        wordsPerMinute: number;
        time: number;
    }) => {
        // נתונים מחושבים מתקבלים מ-ReadingModeTextArea
    }, []);

    useEffect(() => {
        setTimeout(() => {
            setFade("fade-in");
        }, 200);
    }, []);

    // יציאה ממצב קריאה - כאן משחררים את המיקרופון באמת (בין פסוקים הוא נשאר פתוח)
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            releaseMicrophone?.().catch((error: unknown) => {
                console.warn('שגיאה בשחרור המיקרופון:', error);
            });
        };
    }, [releaseMicrophone]);

    /**
     * הצעה לאורח להירשם עולה רק כשהקריאה נגמרה. אם הזרימה הרציפה דלוקה,
     * עוצרים אותה קודם: עדיף שהילד יראה את ההצעה ולא שפסוק חדש יתחיל מאחוריה.
     */
    const showGuestPromptIfPending = useCallback(() => {
        if (!isGuestSignInPromptPending) return false;
        setIsGuestSignInPromptPending(false);
        setIsAutoFlowStopped(true);
        setIsGuestSignInPromptOpen(true);
        return true;
    }, [isGuestSignInPromptPending, setIsGuestSignInPromptOpen, setIsGuestSignInPromptPending]);

    /** סיום מוחלט - חזרה למסך התצוגה המקדימה */
    const finishSession = useCallback(() => {
        setIsDoneListeningDialogOpen(false);
        showGuestPromptIfPending();
        clearData();
        setFade("fade-out");
        endHandler(mode);
    }, [clearData, endHandler, mode, setIsDoneListeningDialogOpen, showGuestPromptIfPending]);

    /** קריאה נוספת של אותו פסוק, בלי לצאת ממצב הקריאה */
    const readCurrentVerseAgain = useCallback(() => {
        setIsDoneListeningDialogOpen(false);
        if (showGuestPromptIfPending()) return;
        setReadIndex((previous) => previous + 1);
        setRunKey((previous) => previous + 1);
    }, [setIsDoneListeningDialogOpen, showGuestPromptIfPending]);

    /**
     * מחכה שהפסוק הנבחר באמת יתחלף. כשעוברים בין פרקים הטעינה אסינכרונית,
     * ולכן לא מספיק לקרוא ל-goNextOrPrevEntity ולהמשיך מיד.
     */
    const waitForEntityChange = useCallback(
        (previousEntityKey: string) =>
            new Promise<boolean>((resolve) => {
                const startedAt = Date.now();
                const check = () => {
                    if (!isMountedRef.current) {
                        resolve(false);
                        return;
                    }
                    if (getEntityKey(booksStore.selectedTextEntity) !== previousEntityKey) {
                        resolve(true);
                        return;
                    }
                    if (Date.now() - startedAt >= ENTITY_CHANGE_TIMEOUT_MS) {
                        resolve(false);
                        return;
                    }
                    setTimeout(check, ENTITY_CHANGE_POLL_MS);
                };
                setTimeout(check, ENTITY_CHANGE_POLL_MS);
            }),
        [booksStore]
    );

    /** מעבר לפסוק הבא בלי לצאת ממצב הקריאה */
    const moveToNextVerse = useCallback(async () => {
        const previousEntityKey = getEntityKey(booksStore.selectedTextEntity);

        setIsDoneListeningDialogOpen(false);
        if (showGuestPromptIfPending()) return;
        setIsAdvancing(true);
        goNextOrPrevEntity(true);

        const hasMoved = await waitForEntityChange(previousEntityKey);
        if (!isMountedRef.current) return;

        setIsAdvancing(false);

        // אין פסוק הבא (סוף הפרק/הספר) - חוזרים למסך התצוגה המקדימה
        if (!hasMoved) {
            finishSession();
            return;
        }

        setReadIndex(1);
        setVersesInFlow((previous) => previous + 1);
        setRunKey((previous) => previous + 1);
    }, [booksStore, finishSession, goNextOrPrevEntity, setIsDoneListeningDialogOpen, showGuestPromptIfPending, waitForEntityChange]);

    /** מה שקורה כשהספירה לאחור מסתיימת, או כשלוחצים "המשך עכשיו" */
    const handleAutoContinue = useCallback(() => {
        if (nextAutoAction === 'again') {
            readCurrentVerseAgain();
        } else {
            void moveToNextVerse();
        }
    }, [moveToNextVerse, nextAutoAction, readCurrentVerseAgain]);

    return (
        <>
            <GuestSignInPromptModal
                isOpen={isGuestSignInPromptOpen}
                onClose={() => setIsGuestSignInPromptOpen(false)}
            />
            <ScoreAndResultDialog
                score={score}
                isOpen={isDoneListeningDialogOpen}
                onClose={finishSession}
                onReadAgain={isAutoFlowOn ? readCurrentVerseAgain : finishSession}
                onNextPassage={
                    isAutoFlowOn
                        ? () => void moveToNextVerse()
                        : () => {
                              goNextOrPrevEntity(true);
                              finishSession();
                          }
                }
                autoFlow={{
                    active: isAutoFlowOn && !isMicrophoneOffDialogOpen,
                    onContinueNow: handleAutoContinue,
                }}
            />
            <MicrophoneOffModal
                isOpen={isMicrophoneOffDialogOpen}
                onClose={() => {
                    setIsMicrophoneOffDialogOpen(false);
                }}
            />
            <div
                className={`reading-mode container type-${mode} ${fade}`}
                onClick={stopAutoFlowOnTouch}
            >
                <div className="card-header">
                    <div className="column">
                        <img 
                            {...(mode === "reading" ? { width: "54", height: "54" } : {})}
                            src={mode === "reading" ? EyeOpen : EyeClose} 
                            alt={mode === "reading" ? t("openEye") : t("closeEye")} 
                        />
                        {mode === "reading" ? 
                            <h3>{t('correctReading')}</h3> : 
                            <h4>{t("recitationByHeart")}</h4>
                        }
                    </div>
                    <div className="column" style={{ gap: "6px" }}>
                        <h4>{t("averageScore")}</h4>
                        <ScoreStars value={Number(stats?.average_score)} />
                    </div>
                </div>
                {isAutoFlowStopped && autoFlowEnabled && (
                    <p className="auto-flow-stopped">{t('autoFlowStoppedNotice')}</p>
                )}
                <div className="content">
                    {isAdvancing ? (
                        <p className="auto-flow-advancing">{t('autoFlowLoadingNextVerse')}</p>
                    ) : (
                        <ReadingModeTextArea
                            key={runKey}
                            mode={mode as 'reading' | 'byHeart'}
                            onScoreCalculated={handleScoreCalculated}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default observer(ReadingModeContainer);
